import { useDroppable } from '@dnd-kit/core';
import type { VirtualItem } from '@tanstack/react-virtual';
import { cn } from '@tempo-io/sui-class-utils';
import { forwardRef, useContext } from 'react';
import { ColRowHighlightContext, useSwimlaneRootProps, useSwimlaneState } from '../../context';
import { buildCellId } from '../../utils';
import {
  CELL_DRAG_OVER_COLOR,
  CELL_GAP,
  COL_ROW_HIGHLIGHT_COLOR,
  IMPLICIT_ROW_ID,
  SORTABLE_TYPES,
} from '../constants';
import { Cards } from './cards/Cards';
import { getCellWidth } from './cards/CardUtils';

interface ISwimlaneGridCell {
  virtualColumn: VirtualItem;
  virtualRow: VirtualItem;
}

export function SwimlaneGridCell({ virtualColumn, virtualRow }: ISwimlaneGridCell) {
  const { rowHeaders, columnHeaders, isCompact, showGridLines, readOnly } = useSwimlaneRootProps();
  const { getRowHeight } = useSwimlaneState();
  const { colHighlightIndex, rowHighlightIndex } = useContext(ColRowHighlightContext);

  const rowID = rowHeaders[virtualRow.index]?.id ?? IMPLICIT_ROW_ID;
  const columnID = columnHeaders[virtualColumn.index]?.id ?? '';
  const cellId = buildCellId({ rowID, columnID });
  const cellIdentifier = `cell-${cellId}`;

  const { setNodeRef, isOver } = useDroppable({
    id: cellIdentifier,
    data: {
      type: SORTABLE_TYPES.Cell,
      cellId,
      accepts: [SORTABLE_TYPES.Card],
    },
    disabled: readOnly,
  });

  const isRowGridLineVisible =
    showGridLines && rowHeaders.length > 0 && rowHeaders.length !== virtualRow.index + 1;

  const isBottomRow = virtualRow.index === Math.max(rowHeaders.length, 1) - 1;
  const isEndColumn = virtualColumn.index === columnHeaders.length - 1;

  const bottomRowExtraStyles = {
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
  };

  const endColumnExtraStyles = {
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
  };

  const isColumnHighlighted = colHighlightIndex === virtualColumn.index;
  const columnHighLightStyles = {
    background: COL_ROW_HIGHLIGHT_COLOR,
    outline: `6px solid ${COL_ROW_HIGHLIGHT_COLOR}`,
    boxShadow: `0px -4px 0px 6px ${COL_ROW_HIGHLIGHT_COLOR}`, // vertically displace shadow by -4px to cover the gaps between cells
    ...(isBottomRow ? bottomRowExtraStyles : {}),
  };

  const isRowHighlighted = rowHighlightIndex === virtualRow.index;
  const rowHighLightStyles = {
    background: COL_ROW_HIGHLIGHT_COLOR,
    outline: `6px solid ${COL_ROW_HIGHLIGHT_COLOR}`,
    boxShadow: `-4px 0px 0px 6px ${COL_ROW_HIGHLIGHT_COLOR}`, // vertically displace shadow by -4px to cover the gaps between cells
    ...(isEndColumn ? endColumnExtraStyles : {}),
  };

  const rowHeight = getRowHeight(rowID);

  const columnHeader = columnHeaders[virtualColumn.index];
  const rowHeader = rowHeaders[virtualRow.index];

  return (
    <GridCell
      key={virtualColumn.key}
      data-column={virtualColumn.index}
      data-row={virtualRow.index}
      data-testid="grid-cell"
      data-column-id={columnID}
      ref={setNodeRef}
      style={{
        top: `${virtualRow.start}px`,
        left: `${virtualColumn.start}px`,
        minHeight: `${rowHeight}px`,
        minWidth: `${getCellWidth(isCompact)}px`,
        transformStyle: 'preserve-3d',
        background: isOver ? CELL_DRAG_OVER_COLOR : 'transparent',
        ...(isColumnHighlighted ? columnHighLightStyles : {}),
        ...(isRowHighlighted ? rowHighLightStyles : {}),
      }}
      isRowGridLineVisible={isRowGridLineVisible}
    >
      {columnHeader && (
        <Cards cellTop={virtualRow.start} columnHeader={columnHeader} rowHeader={rowHeader} />
      )}
    </GridCell>
  );
}

// Vertical column dividers are drawn at the SwimlaneGrid level so
// they span uninterrupted across group-header / empty rows. Only the horizontal
// row divider lives here, per-cell.
interface GridCellProps extends React.HTMLAttributes<HTMLDivElement> {
  isRowGridLineVisible: boolean;
}

const GridCell = forwardRef<HTMLDivElement, GridCellProps>(
  ({ isRowGridLineVisible, className, style, ...props }, ref) => {
    const gridLineStyle = isRowGridLineVisible
      ? ({ '--grid-line-offset': `-${CELL_GAP.value / 2}px` } as React.CSSProperties)
      : undefined;
    return (
      <div
        ref={ref}
        style={gridLineStyle ? { ...style, ...gridLineStyle } : style}
        className={cn(
          'absolute left-0 top-0',
          isRowGridLineVisible && [
            'after:content-[""] after:block after:absolute after:bottom-0 after:h-px',
            'after:left-0 after:right-0',
            'after:[margin-bottom:var(--grid-line-offset)]',
            'after:[margin-left:var(--grid-line-offset)]',
            'after:[margin-right:var(--grid-line-offset)]',
            'after:border-b after:border-border-neutral-bold/12 after:z-0',
            'after:transform-[translateZ(-1px)]',
          ],
          className,
        )}
        {...props}
      />
    );
  },
);
